/**
 * The coach's own state: what has been retired, and what the card is showing.
 *
 * The cues themselves are DERIVED and never frozen (see `cues.ts`), so nothing
 * here holds a cue. What is held is the state the document cannot supply: which
 * run finished, which visible cue the card is on, and the retirement a person has
 * accumulated by acting, skipping and muting.
 *
 * NEVER PERSISTED, deliberately — the `lastScan` precedent. A mute is a reading
 * decision taken about the graph as it stood, and asserting it again three weeks
 * later, over a document that has since been rewritten, would be the app
 * remembering something the user never told it to keep. Honest loss on reload is
 * the cheaper failure, and it costs one keypress to re-take.
 *
 * The two identities are the whole design and they are not interchangeable: `seen`
 * is keyed by cue key (one instance) and `skips`/`muted` by kind. Acting on one
 * `thin` must not silence the next one; skipping the kind twice must. The key is
 * opaque here on purpose — `cues.ts` decides what makes an instance, and it names
 * the record where there is one, so answering the cue about one bridge does not
 * put away the next bridge on the same claim.
 *
 * Pure. Every helper takes `at` rather than reading the clock, so the store is the
 * one caller of `Date.now()` and a test can state a timeline. It imports `./cues`
 * and `./types` and nothing else, for the reason `cues.ts` imports so little: the
 * store imports THIS, so it has to stay a leaf.
 */

import {
  assembleCues,
  cueSetFor,
  SKIPS_TO_MUTE,
  type Cue,
  type CueActionKind,
  type CueContext,
  type CueKind,
  type CueLens,
} from './cues'
import type { Session } from './types'

/** One line of the session's cue history. `key` is the instance it was taken on;
 *  a mute with no cue open records the kind in that slot instead. */
export interface CoachEntry {
  key: string
  kind: CueKind
  at: number
  how: 'acted' | 'skipped' | 'muted'
}

export interface CoachState {
  sessionId: string
  /** The run that finished, or null when opened by hand with none finished. The
   *  kinds that read a run (`flat`, `decide`) simply do not fire in that case. */
  sourceRunId: string | null
  /** The card. */
  open: boolean
  /** Which of the visible cues the card is showing. */
  index: number
  /** The digest list instead of the pins. The card and the digest exclude each
   *  other: two surfaces reading out the same list at once is the crowding this
   *  layer exists to avoid. */
  digest: boolean
  /** The WHY fold on the open card. */
  why: boolean
  lens: CueLens
  /** Retired instances, by cue key. */
  seen: Record<string, 1>
  /** Skips per KIND — twice and the kind mutes. See `SKIPS_TO_MUTE`. */
  skips: Record<string, number>
  /** Muted kinds. */
  muted: Record<string, 1>
  /** The coach raised over the graph — its pins — by a run's close or Guide me.
   *  Named for the run-done bar that once showed it, which folded into the run
   *  strip and the coach's quiet line; dismissable on its own (the line's ✕,
   *  Escape's last rung), and it survives the card closing. */
  bar: boolean
  log: CoachEntry[]
}

/**
 * The state a finishing run — or a press of Guide me — opens with.
 *
 * Retirement is SESSION-scoped, not run-scoped: someone who muted `prune` after
 * the first run has said what they think of `prune` in this exploration, and
 * making them say it again after every run would make the mute worthless. So
 * `seen`/`skips`/`muted`/`lens`/`log` carry over from `prior`, but only while the
 * session id is the same one — a different exploration is a different graph and
 * starts clean, at the default lens.
 */
export function freshCoach(
  sessionId: string,
  sourceRunId: string | null,
  prior: CoachState | null,
): CoachState {
  const carried = prior && prior.sessionId === sessionId ? prior : null
  return {
    sessionId,
    sourceRunId,
    open: true,
    index: 0,
    digest: false,
    why: false,
    lens: carried?.lens ?? 'risk',
    seen: { ...(carried?.seen ?? {}) },
    skips: { ...(carried?.skips ?? {}) },
    muted: { ...(carried?.muted ?? {}) },
    bar: true,
    log: carried ? carried.log.slice() : [],
  }
}

/**
 * Retire one cue instance, by acting on it or by skipping it.
 *
 * Both mark the instance seen — a cue you acted on and a cue you declined are
 * equally answered — and only a skip counts against the kind. The index goes back
 * to the top because the list under it has just changed length, and the WHY fold
 * closes because it was open about a cue that is no longer on screen.
 */
export function coachRetire(
  c: CoachState,
  key: string,
  kind: CueKind,
  how: 'acted' | 'skipped',
  at: number,
): CoachState {
  return {
    ...c,
    seen: { ...c.seen, [key]: 1 },
    skips: how === 'skipped' ? { ...c.skips, [kind]: (c.skips[kind] ?? 0) + 1 } : c.skips,
    log: [...c.log, { key, kind, at, how }],
    index: 0,
    why: false,
  }
}

/**
 * Silence a whole kind for this session, immediately.
 *
 * The press lives in the WHY fold, so a `key` is usually at hand and the log
 * records which instance the decision was taken on. Where there is none — a mute
 * from a list rather than from an open card — the kind stands in that slot, so a
 * log line always names something.
 */
export function coachMute(c: CoachState, kind: CueKind, at: number, key?: string): CoachState {
  return {
    ...c,
    muted: { ...c.muted, [kind]: 1 },
    log: [...c.log, { key: key ?? kind, kind, at, how: 'muted' }],
    index: 0,
    why: false,
  }
}

/**
 * The blanket reset — the digest's foot, and the quiet line once the board is
 * answered: everything retired comes back.
 *
 * All three maps, because a person pressing "unmute" after skipping their way
 * through a board means "show me it again", and leaving `seen` behind would give
 * them a board that is still mostly empty. The log is KEPT — it is the history of
 * what was done, and unmuting does not un-happen it.
 */
export function coachUnmute(c: CoachState): CoachState {
  return { ...c, seen: {}, skips: {}, muted: {}, index: 0 }
}

/**
 * What this exploration has put away, in the two shapes the digest reports.
 *
 * `silenced` is per KIND and covers both routes into silence — an explicit mute,
 * and a kind skipped `SKIPS_TO_MUTE` times — because both are silence and one
 * button is the way back from both. `retired` is per INSTANCE and is the count
 * the bar was missing: on a board where every kind has one instance, answering
 * each cue once empties it without silencing a single kind, and a way-back
 * offered only against `silenced` is disabled at exactly the moment it is
 * needed. `coachUnmute` clears all three maps, so the button can honour what its
 * title promises — skipped or silenced, everything comes back.
 *
 * `restorable` is returned rather than recomputed at the button, so the enabling
 * predicate is the one a test can state.
 */
export function coachRetirement(c: CoachState): {
  silenced: CueKind[]
  retired: number
  restorable: boolean
} {
  const silenced = new Set<CueKind>()
  for (const kind of Object.keys(c.muted)) silenced.add(kind as CueKind)
  for (const [kind, n] of Object.entries(c.skips)) {
    if (n >= SKIPS_TO_MUTE) silenced.add(kind as CueKind)
  }
  const retired = Object.keys(c.seen).length
  return { silenced: [...silenced], retired, restorable: silenced.size > 0 || retired > 0 }
}

/**
 * The figures this leaf cannot derive, handed in by the caller.
 *
 * PLAIN DATA, never a callback. The price arithmetic lives in the engine, which
 * this module must not import, so the UI computes it and passes numbers down —
 * and numbers are the form the memo below can compare. A `usdOf` closure here
 * would be a new identity on every render, which would defeat the memo and put a
 * full cue assembly on the render path.
 *
 * `usdByAction` is per ACTION KIND rather than per cue: two `verify` cues cost the
 * same press, and pricing the kind is what keeps the CHEAPEST lens and the figure
 * on the button the same number.
 */
export interface CoachExtras {
  usdByAction?: Partial<Record<CueActionKind, number>>
  capLeftUsd?: number | null
  nextMoveUsd?: number | null
}

const NO_EXTRAS: CoachExtras = {}

/** Shallow key/value equality over the price table — the same figures under the
 *  same action kinds is the same table, whoever built the object. */
function sameUsdByAction(
  a: Partial<Record<CueActionKind, number>> | undefined,
  b: Partial<Record<CueActionKind, number>> | undefined,
): boolean {
  if (a === b) return true
  const ak = Object.keys(a ?? {}) as CueActionKind[]
  const bk = Object.keys(b ?? {}) as CueActionKind[]
  if (ak.length !== bk.length) return false
  for (const k of ak) if (a?.[k] !== b?.[k]) return false
  return true
}

/** The two dollar fields compare by value, with absent and null read as the same
 *  fact: there is no cap, or there is no projection. */
function sameExtras(a: CoachExtras, b: CoachExtras): boolean {
  return (
    (a.capLeftUsd ?? null) === (b.capLeftUsd ?? null) &&
    (a.nextMoveUsd ?? null) === (b.nextMoveUsd ?? null) &&
    sameUsdByAction(a.usdByAction, b.usdByAction)
  )
}

/** The last (session, coach, focusId, extras) call and what it assembled. */
let memo: {
  session: Session
  coach: CoachState
  focusId: string | null
  extras: CoachExtras
  cues: Cue[]
} | null = null

/**
 * The visible cues, for this document, this retirement state and these figures.
 *
 * Memoized on the IDENTITY of the first three inputs — every store write that
 * could change the answer swaps one of them, and nothing else can — and on the
 * VALUE of the fourth, because the prices are rebuilt from the routing view rather
 * than held in the store, so a caller that recomputes an equal table must still
 * get the cached array back. One entry is enough: there is one coach.
 *
 * `capLeftUsd`/`nextMoveUsd` go straight through to the `decide` gate, so the
 * question "is what is left under your ceiling short of one more move?" is asked
 * of the same numbers the buttons print.
 */
export function coachCues(
  session: Session,
  coach: CoachState,
  focusId: string | null,
  extras: CoachExtras = NO_EXTRAS,
): Cue[] {
  if (
    memo &&
    memo.session === session &&
    memo.coach === coach &&
    memo.focusId === focusId &&
    sameExtras(memo.extras, extras)
  ) {
    return memo.cues
  }
  const ctx: CueContext = {
    run: coach.sourceRunId ? (session.runs.find((r) => r.id === coach.sourceRunId) ?? null) : null,
    focusId,
    capLeftUsd: extras.capLeftUsd,
    nextMoveUsd: extras.nextMoveUsd,
  }
  const cues = assembleCues(
    cueSetFor(session, ctx),
    { seen: coach.seen, skips: coach.skips, muted: coach.muted },
    coach.lens,
    (c) => (c.action ? (extras.usdByAction?.[c.action.kind] ?? 0) : 0),
  )
  memo = { session, coach, focusId, extras, cues }
  return cues
}
